/**
 * Image セクション。
 *
 * variants:
 *   - gallery_carousel: 1 枚ずつ表示、下部ドットナビゲータ + 左右矢印で切替
 *   - grid_3col:        3 列グリッド (3-6 枚)
 *   - full_width:       フル幅 1 枚
 *
 * NOTE: items は ランディング既存の `image_list` UI field と互換させるため、
 *       URL の string 配列（`string[]`）として扱う。
 *       将来キャプション付きにする場合は `image_caption_list` 等の
 *       新フィールド型を導入する方針。
 */
import { z } from 'zod';
import type { AISchemaField, UISchemaField } from '../types/index';

export const imageAISchema: AISchemaField[] = [
  { name: 'eyebrow', type: 'string', required: false, max: 30 },
  { name: 'headline', type: 'string', required: false, max: 80 },
  { name: 'subheadline', type: 'string', required: false, max: 200 },
  {
    name: 'items',
    type: 'array<image_url>',
    required: true,
    max: 12,
    hint: '画像 URL の配列。AI 生成時は空文字を返し、後で手動アップロード。',
  },
  { name: 'caption', type: 'string', required: false, max: 120, hint: 'ギャラリー全体のキャプション (任意)' },
  {
    name: 'imageFit',
    type: 'string',
    required: false,
    max: 10,
    hint: '画像の表示方法: "cover" (枠に合わせて切取、デフォルト) / "contain" (全体表示+余白) / "natural" (画像比率を維持)',
  },
];

export const imageUISchema: UISchemaField[] = [
  { name: 'eyebrow', type: 'text', label: 'アイブロウ', max: 30 },
  { name: 'headline', type: 'text', label: '見出し', max: 80 },
  { name: 'subheadline', type: 'textarea', label: 'サブ見出し', max: 200 },
  { name: 'items', type: 'image_list', label: '画像リスト', required: true, max: 12 },
  { name: 'caption', type: 'text', label: 'キャプション (任意)', max: 120 },
  { name: 'imageFit', type: 'select', label: '画像の表示方法', options: [
    { value: 'cover', label: 'カバー (枠に合わせて切り取り、現行デフォルト)' },
    { value: 'contain', label: 'コンテイン (画像全体を表示、余白あり)' },
    { value: 'natural', label: 'ナチュラル (画像比率を維持、枠が伸縮)' },
  ] },
];

export const imageZodSchema = z.object({
  eyebrow: z.string().max(30).optional().nullable(),
  headline: z.string().max(80).optional().nullable(),
  subheadline: z.string().max(200).optional().nullable(),
  items: z.array(z.string()).min(1).max(12),
  caption: z.string().max(120).optional().nullable(),
  imageFit: z.enum(['cover', 'contain', 'natural']).optional().nullable(),
});

export type ImageContent = z.infer<typeof imageZodSchema>;

export const imageSample: ImageContent = {
  eyebrow: 'Gallery',
  headline: 'ビジュアルギャラリー',
  subheadline: '画像で魅力を伝えるシンプルなセクション。',
  items: ['', '', ''],
  caption: null,
  imageFit: 'cover',
};
